import React, {
  cloneElement,
  CSSProperties,
  forwardRef,
  FunctionComponent,
  useEffect
} from 'react';
import { createPortal } from 'react-dom';
import { useBarcode } from 'react-barcodes';
import QRCode from 'react-qr-code';
import { ShippingLabelByPharmacistService } from '@/services/phamacy/interface';

interface Props {
  data: ShippingLabelByPharmacistService;
  closeTeamFreshShippingLabelHandle: () => void;
}

interface DefaultComponentProps {
  style?: CSSProperties;
}

const PrintTeamFreshShippingLabel = ({
  data: {
    agencyOrderNumber,
    areaGroupCode,
    areaCode,
    deliveryType,
    hubName,
    receiver,
    sender,
    trackingNumber,
    deliveryMemo
  },
  closeTeamFreshShippingLabelHandle
}: Props) => {
  const containerElement = document.createElement('div');

  const { inputRef: barcodeRef } = useBarcode({
    value: trackingNumber,
    options: {
      displayValue: false,
      background: 'transparent',
      width: 1.5,
      height: 38
    }
  });

  useEffect(() => {
    const externalWindow: WindowProxy | null = window.open(
      '',
      '',
      'width=1200,height=1200,left=200,top=200'
    );
    if (externalWindow) {
      const css = `@media 100mm 199mm {
            @page {size: 100mm 199mm; margin: 0; padding: 0;}
          }
          
          html, body {margin: 0; padding: 0; display: grid;}
            .label {width: 100mm; height: 195mm;}
            .label:not(:first-of-type) { margin-top: 3mm !important; }`;
      const head =
        externalWindow.document.head ||
        document.getElementsByTagName('head')[0];
      const style = externalWindow.document.createElement('style');
      style.type = 'text/css';
      style.appendChild(document.createTextNode(css));
      head.appendChild(style);

      externalWindow.document.body.appendChild(containerElement);
      externalWindow.onafterprint = () => {
        setTimeout(() => {
          closeTeamFreshShippingLabelHandle();
          externalWindow.close();
        }, 200);
      };

      externalWindow.print();
    }
  }, []);

  return (
    <>
      {createPortal(
        cloneElement(
          <Label>
            <HubName>{hubName}</HubName>
            <BrandName>닥터나우</BrandName>
            <TrackingNumber>{trackingNumber}</TrackingNumber>
            <Barcode ref={barcodeRef} />

            <DeliveryType>{deliveryType}</DeliveryType>
            <Area>{areaCode}</Area>
            <AreaGroupCode>{areaGroupCode}</AreaGroupCode>
            <QRCodeSection value={trackingNumber} />

            <Count>1/1</Count>
            <OrderNumber>
              <TextSpace>주 문 번 호</TextSpace> {agencyOrderNumber}
            </OrderNumber>
            <SenderName>
              <TextSpace>보 내 는 분</TextSpace> {sender.name}
            </SenderName>
            <ReceiverName>
              <TextSpace>받 는 분</TextSpace> {receiver.name}님
            </ReceiverName>
            <ProductInfo>
              <TextSpace>처 방 약</TextSpace> [1개]
            </ProductInfo>
            <DeliveryMemo>
              <TextSpace>요 청 사 항</TextSpace>{' '}
              {deliveryMemo ? deliveryMemo : '안전하게 배송해주세요'}
            </DeliveryMemo>
            <ReceiverAddress>
              <TextSpace>주 소</TextSpace> {receiver.address}
            </ReceiverAddress>
          </Label>,
          {}
        ),
        containerElement
      )}
    </>
  );
};

export default PrintTeamFreshShippingLabel;

const Label: FunctionComponent = ({ children }) => (
  <div
    className="label"
    style={{
      position: 'relative',
      width: '99mm',
      height: '199mm',
      margin: 0,
      padding: 0
    }}>
    {children}
  </div>
);

const DefaultComponent: FunctionComponent<DefaultComponentProps> = ({
  children,
  style = {}
}) => {
  const defaultStyle = {
    transform: 'rotate(-90deg)',
    transformOrigin: 'left top',
    position: 'absolute',
    margin: '0',
    fontSize: '10px',
    whiteSpace: 'pre-wrap',
    lineHeight: '3mm'
  } as CSSProperties;

  return <div style={{ ...defaultStyle, ...style }}>{children}</div>;
};

const Barcode = forwardRef<HTMLCanvasElement>((props, ref) => (
  <DefaultComponent
    style={{
      top: '80mm',
      left: '6.5mm'
    }}>
    <canvas ref={ref} {...props} />
  </DefaultComponent>
));

const HubName: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '174mm',
      left: '14mm',
      fontSize: '20px',
      fontWeight: 'bold'
    }}>
    {children}
  </DefaultComponent>
);

const BrandName: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '150mm',
      left: '13mm',
      fontSize: '30px',
      fontWeight: 'bold'
    }}>
    {children}
  </DefaultComponent>
);

const TrackingNumber: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '59mm',
      left: '5.5mm',
      fontSize: '12px'
    }}>
    {children}
  </DefaultComponent>
);

const DeliveryType: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '175mm',
      left: '24mm',
      fontSize: '24px',
      fontWeight: 'bold'
    }}>
    {children}
  </DefaultComponent>
);

const AreaGroupCode: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '180mm',
      left: '54mm',
      fontSize: '24px',
      fontWeight: 'bold'
    }}>
    {children}
  </DefaultComponent>
);

const OrderNumber: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '148mm',
      left: '23mm',
      fontSize: '12px'
    }}>
    {children}
  </DefaultComponent>
);

const SenderName: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '148mm',
      left: '28mm',
      fontSize: '12px'
    }}>
    {children}
  </DefaultComponent>
);

const ReceiverName: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '148mm',
      left: '33mm',
      fontSize: '12px'
    }}>
    {children}
  </DefaultComponent>
);

const DeliveryMemo: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '148mm',
      left: '43mm',
      fontSize: '12px',
      width: '115mm'
    }}>
    {children}
  </DefaultComponent>
);

const ReceiverAddress: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '148mm',
      left: '48mm',
      fontSize: '12px',
      width: '115mm'
    }}>
    {children}
  </DefaultComponent>
);

const ProductInfo: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '148mm',
      left: '38mm',
      fontSize: '12px'
    }}>
    {children}
  </DefaultComponent>
);

const TextSpace: FunctionComponent = ({ children }) => (
  <span
    style={{
      display: 'inline-block',
      width: '18mm',
      fontSize: '12px',
      fontWeight: 'bold'
    }}>
    {children}
  </span>
);

const Count: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '15mm',
      left: '23mm',
      fontSize: '12px',
      fontWeight: 'bold'
    }}>
    {children}
  </DefaultComponent>
);
const QRCodeSection: FunctionComponent<{ value: string }> = ({ value }) => (
  <DefaultComponent
    style={{
      top: '183mm',
      left: '63mm',
      background: 'transparent'
    }}>
    <QRCode value={value} size={100} fgColor="#000000" />
  </DefaultComponent>
);

const Area: FunctionComponent = ({ children }) => (
  <DefaultComponent
    style={{
      top: '174mm',
      left: '39mm',
      fontSize: '60px',
      fontWeight: 'bold'
    }}>
    {children}
  </DefaultComponent>
);
